import React, { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { AlertTriangle, RefreshCcw, Home } from 'lucide-react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI.
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo);
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  private handleGoHome = () => {
    this.setState({ hasError: false, error: null });
    window.location.href = '/';
  };

  public render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-background flex flex-col items-center justify-center p-6 text-center animate-in fade-in duration-500">
          <div className="w-20 h-20 bg-destructive/10 rounded-full flex items-center justify-center mb-6">
            <AlertTriangle className="w-10 h-10 text-destructive" />
          </div>

          <h1 className="text-2xl font-bold tracking-tighter sm:text-3xl mb-2">
            Something went wrong
          </h1>

          <p className="text-muted-foreground max-w-md mb-8">
            An unexpected error occurred. This might be a temporary issue. Try refreshing the page
            or going back to the dashboard.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 w-full max-w-xs">
            <Button onClick={this.handleReset} className="flex-1 gap-2">
              <RefreshCcw className="w-4 h-4" />
              Refresh Page
            </Button>

            <Button
              variant="outline"
              onClick={this.handleGoHome}
              className="flex-1 gap-2 border-border/40 hover:bg-accent/50"
            >
              <Home className="w-4 h-4" />
              Go to Home
            </Button>
          </div>

          <div className="mt-12 p-4 bg-muted/30 rounded-lg border border-border/10 text-left w-full max-w-xl overflow-hidden">
            <p className="text-[10px] uppercase font-bold text-muted-foreground tracking-widest mb-2">
              Error Details (Development Only)
            </p>
            <code className="text-xs text-destructive/80 break-all font-mono">
              {this.state.error?.toString()}
            </code>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
